import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, Animated, Easing, ScrollView, Alert } from 'react-native';
import { BlurView } from 'expo-blur';
import { GlassBackgroundGradient, GradientOverlay } from '../components/Gradients';
import { ScreenBackground } from '../components/ThemeGradients';
import NavigationHeader from '../components/NavigationHeader';
import GradientButton from '../components/GradientButton';
import GradientText from '../components/GradientText';
import { useNavigation } from '../context/NavigationContext';
import { useTicker } from '../context/TickerContext';
import { handleSubscription, SUBSCRIPTION_PLANS } from '../services/stripeService';
import { useAuth } from '../context/AuthContext';

const PlansScreen = () => {
    const { navigateTo } = useNavigation();
    const { user, userProfile } = useAuth();
    const { isTickerEnabled, totalHeaderHeight } = useTicker();
    // Page load animation
    const pageOpacity = useRef(new Animated.Value(0)).current;
    const pageTranslateY = useRef(new Animated.Value(20)).current;
    
    // Exit animation state
    const [isExiting, setIsExiting] = useState(false);
    
    // Current subscription state
    const [currentPlan, setCurrentPlan] = useState(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    
    // Card hover animations
    const card1GlowT = useRef(new Animated.Value(0)).current;
    const card2GlowT = useRef(new Animated.Value(0)).current;
    const card3GlowT = useRef(new Animated.Value(0)).current;
    
    const card1ScaleT = useRef(new Animated.Value(1)).current;
    const card2ScaleT = useRef(new Animated.Value(1)).current;
    const card3ScaleT = useRef(new Animated.Value(1)).current;

    // Page load animation effect
    useEffect(() => {
        Animated.parallel([
            Animated.timing(pageOpacity, {
                toValue: 1,
                duration: 1200,
                easing: Easing.inOut(Easing.cubic),
                useNativeDriver: true,
            }),
            Animated.timing(pageTranslateY, {
                toValue: 0,
                duration: 1000,
                easing: Easing.inOut(Easing.cubic),
                useNativeDriver: true,
            }),
        ]).start();
    }, []);


    // Check user's current subscription
    useEffect(() => {
        if (userProfile?.subscription) {
            const subscription = userProfile.subscription;
            setSubscriptionStatus(subscription.subscriptionStatus);
            
            // Determine current plan
            if (subscription.planType) {
                setCurrentPlan(subscription.planType);
            } else if (subscription.subscriptionStatus === 'active') {
                // If no planType but status is active, we can try to determine from other data
                setCurrentPlan('ACTIVE');
            } else {
                setCurrentPlan('FREE');
            }
        } else {
            setCurrentPlan('FREE');
            setSubscriptionStatus('none');
        }
    }, [userProfile]);

    // Register exit animation with navigation context
    useEffect(() => {
        if (Platform.OS === 'web') {
            window.plansExitAnimation = exitAnimation;
        }
        
        return () => {
            if (Platform.OS === 'web') {
                delete window.plansExitAnimation;
            }
        };
    }, []);

    // Card entrance animations
    useEffect(() => {
        const cardAnimations = [
            { ref: card1ScaleT, delay: 200 },
            { ref: card2ScaleT, delay: 400 },
            { ref: card3ScaleT, delay: 600 }
        ];

        cardAnimations.forEach(({ ref, delay }) => {
            ref.setValue(0.8);
            Animated.timing(ref, {
                toValue: 1,
                duration: 800,
                delay: delay,
                easing: Easing.out(Easing.back(1.2)),
                useNativeDriver: true,
            }).start();
        });
    }, []);

    const onCardIn = (cardNumber) => {
        if (isExiting) return;
        const glowRef = cardNumber === 1 ? card1GlowT : cardNumber === 2 ? card2GlowT : card3GlowT;
        const scaleRef = cardNumber === 1 ? card1ScaleT : cardNumber === 2 ? card2ScaleT : card3ScaleT;
        
        Animated.timing(glowRef, { 
            toValue: 1, 
            duration: 1500, 
            easing: Easing.bezier(0.22, 1, 0.36, 1), 
            useNativeDriver: true 
        }).start();
        Animated.timing(scaleRef, { 
            toValue: 1.02, 
            duration: 300, 
            easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), 
            useNativeDriver: true 
        }).start();
    };

    const onCardOut = (cardNumber) => {
        if (isExiting) return;
        const glowRef = cardNumber === 1 ? card1GlowT : cardNumber === 2 ? card2GlowT : card3GlowT;
        const scaleRef = cardNumber === 1 ? card1ScaleT : cardNumber === 2 ? card2ScaleT : card3ScaleT;
        
        Animated.timing(glowRef, { 
            toValue: 0, 
            duration: 1500, 
            easing: Easing.bezier(0.22, 1, 0.36, 1), 
            useNativeDriver: true 
        }).start();
        Animated.timing(scaleRef, { 
            toValue: 1, 
            duration: 300, 
            easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), 
            useNativeDriver: true 
        }).start();
    };

    // Exit animation function
    const exitAnimation = (callback) => {
        setIsExiting(true);
        
        Animated.parallel([
            Animated.timing(pageOpacity, {
                toValue: 0,
                duration: 600,
                easing: Easing.in(Easing.cubic),
                useNativeDriver: true,
            }),
            Animated.timing(pageTranslateY, {
                toValue: 20,
                duration: 600,
                easing: Easing.in(Easing.cubic),
                useNativeDriver: true,
            }),
        ]).start(() => {
            if (callback) callback();
        });
    };

    const handleSubscribe = async (planType) => {
        // Check if user is logged in
        if (!user) {
            navigateTo('Login', { redirectAfterLogin: 'Plans' });
            return;
        }

        try {
            // This will redirect to Stripe hosted checkout
            await handleSubscription(planType, user.email);
            
        } catch (error) {
            Alert.alert(
                'Subscription Error',
                'There was an error processing your subscription. Please try again.',
                [{ text: 'OK' }]
            );
            console.error('Subscription error:', error);
        }
    };

    // Determine button text and styling based on current plan
    const getButtonConfig = (planName) => {
        const planType = planName.toUpperCase();
        
        // If user is not logged in, show login prompt
        
        if (subscriptionStatus === 'cancelled' || subscriptionStatus === 'none') {
            // No active subscription
            return {
                text: planType === 'BASIC' ? 'GET STARTED' : 
                      planType === 'PRO' ? 'UPGRADE TO PRO' : 'GO ULTIMATE',
                disabled: false,
                style: {}
            };
        }
        
        if (currentPlan === planType) {
            // Current plan
            return {
                text: 'CURRENT PLAN',
                disabled: true,
                style: { opacity: 0.6 }
            };
        }
        
        if (currentPlan === 'BASIC' && planType === 'PRO') {
            return {
                text: 'UPGRADE TO PRO',
                disabled: false,
                style: {}
            };
        }
        
        if (currentPlan === 'BASIC' && planType === 'ULTIMATE') {
            return {
                text: 'UPGRADE TO ULTIMATE',
                disabled: false,
                style: {}
            };
        }
        
        if (currentPlan === 'PRO' && planType === 'ULTIMATE') {
            return {
                text: 'UPGRADE TO ULTIMATE',
                disabled: false,
                style: {}
            };
        }
        
        if (currentPlan === 'PRO' && planType === 'BASIC') {
            return {
                text: 'DOWNGRADE TO BASIC',
                disabled: false,
                style: {}
            };
        }
        
        if (currentPlan === 'ULTIMATE' && planType === 'BASIC') {
            return {
                text: 'DOWNGRADE TO BASIC',
                disabled: false,
                style: {}
            };
        }
        
        if (currentPlan === 'ULTIMATE' && planType === 'PRO') {
            return {
                text: 'DOWNGRADE TO PRO',
                disabled: false,
                style: {}
            };
        }
        
        // Default fallback
        return {
            text: planType === 'BASIC' ? 'GET STARTED' : 
                  planType === 'PRO' ? 'UPGRADE TO PRO' : 'GO ULTIMATE',
            disabled: false,
            style: {}
        };
    };

    const PlanCard = ({ plan, cardNumber }) => {
        const buttonConfig = getButtonConfig(plan.name);
        const isCurrentPlan = currentPlan === plan.name.toUpperCase();
        
        return (
            <Animated.View
                style={[
                    styles.card,
                    {
                        transform: [{ scale: cardNumber === 1 ? card1ScaleT : cardNumber === 2 ? card2ScaleT : card3ScaleT }]
                    },
                    isCurrentPlan && styles.currentPlanCard
                ]}
                {...(Platform.OS === 'web' ? { 
                    onMouseEnter: () => onCardIn(cardNumber), 
                    onMouseLeave: () => onCardOut(cardNumber) 
                } : {})}
            >
                <GlassBackgroundGradient borderRadius={20} />
                <GradientOverlay 
                    opacity={cardNumber === 1 ? card1GlowT : cardNumber === 2 ? card2GlowT : card3GlowT} 
                    borderRadius={20} 
                />
                <BlurView intensity={60} tint="dark" style={styles.cardBlur}>
                    <View style={styles.cardContent}>
                        <View style={styles.headerSection}>
                            <Text style={styles.planName}>{plan.name}</Text>
                            <GradientText style={styles.planPrice}>{plan.price}</GradientText>
                            <Text style={styles.planPeriod}>{plan.period}</Text>
                        </View>
                        
                        <View style={styles.featuresContainer}>
                            {plan.features.map((feature, index) => (
                                <View key={index} style={styles.featureItem}>
                                    <Text style={styles.checkmark}>✓</Text>
                                    <Text style={styles.featureText}>{feature}</Text>
                                </View>
                            ))}
                        </View>
                        
                        <GradientButton
                            text={buttonConfig.text}
                            onPress={() => !buttonConfig.disabled && handleSubscribe(plan.name.toUpperCase())}
                            style={[styles.subscribeButton, buttonConfig.style]}
                            textStyle={styles.subscribeButtonText}
                            gradientColors={buttonConfig.disabled ? ["#555555", "#777777"] : ["#707070", "#EFEFEF"]}
                            fontSize={16}
                            fontFamily="ClimateCrisis"
                            fontWeight="400"
                            disabled={buttonConfig.disabled}
                        />
                    </View>
                </BlurView>
            </Animated.View>
        );
    };

    const plans = [
        {
            name: 'BASIC',
            price: '$9.99',
            period: 'per month',
            buttonText: 'GET STARTED',
            features: [
                '100 queries per month',
                'SGP optimization',
                'Multi-sportsbook comparison',
                'AI-powered odds analysis',
                'Real-time betting data',
                'EV calculations',
                'Win probability metrics'
            ]
        },
        {
            name: 'PRO',
            price: '$29.99',
            period: 'per month',
            buttonText: 'UPGRADE TO PRO',
            features: [
                '250 queries per month',
                'SGP optimization',
                'Multi-sportsbook comparison',
                'AI-powered odds analysis',
                'Real-time betting data',
                'EV calculations',
                'Win probability metrics',
                'Advanced market coverage',
                'Priority support'
            ]
        },
        {
            name: 'ULTIMATE',
            price: '$99.99',
            period: 'per month',
            buttonText: 'GO ULTIMATE',
            features: [
                'Unlimited queries',
                'SGP optimization',
                'Multi-sportsbook comparison',
                'AI-powered odds analysis',
                'Real-time betting data',
                'EV calculations',
                'Win probability metrics',
                'Advanced market coverage',
                'Early access to new features',
                'Priority support',
                'Dedicated account manager'
            ]
        }
    ];

    return (
        <View style={styles.container}>
            <ScreenBackground />
            <View style={[styles.splashContainer, { 
                paddingTop: Platform.OS === 'web' ? (totalHeaderHeight + 32) : 16 
            }]}>
                <NavigationHeader />
                
                <Animated.View 
                    style={[
                        styles.contentContainer,
                        {
                            opacity: pageOpacity,
                            transform: [{ translateY: pageTranslateY }]
                        }
                    ]}
                >
                    <View style={styles.headerContainer}>
                        <Text style={styles.pageTitle}>CHOOSE YOUR PLAN</Text>
                        <Text style={styles.pageSubtitle}>
                            Unlock the power of advanced betting optimization with our premium plans
                        </Text>
                    </View>

                    <ScrollView 
                        style={styles.scrollContainer}
                        contentContainerStyle={styles.scrollContent}
                        showsVerticalScrollIndicator={false}
                    >
                        <View style={styles.cardContainer}>
                            <PlanCard plan={plans[0]} cardNumber={1} />
                            <PlanCard plan={plans[1]} cardNumber={2} />
                            <PlanCard plan={plans[2]} cardNumber={3} />
                        </View>
                        
                        <View style={styles.freeNoticeContainer}>
                            <Text style={styles.freeNoticeTitle}>Free Plan</Text>
                            <Text style={styles.freeNoticeText}>
                                Try Bet Optimized with 10 free queries per month. No credit card required.
                            </Text>
                        </View>
                    </ScrollView>
                </Animated.View>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#000000',
        minHeight: '100vh',
    },
    background: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#000000',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
    },
    splashContainer: {
        flex: 1,
        width: '100%',
        height: '100%',
        paddingHorizontal: Platform.OS === 'web' ? 48 : 24,
        paddingTop: Platform.OS === 'web' ? 32 : 16,
        paddingBottom: Platform.OS === 'web' ? 48 : 24,
        gap: 48,
    },
    contentContainer: {
        width: '100%',
        height: '100%',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 40,
        paddingVertical: 20,
    },
    headerContainer: {
        alignItems: 'center',
        gap: 16,
    },
    pageTitle: {
        color: 'rgba(255, 255, 255, 0.90)',
        fontSize: 48,
        fontFamily: 'ClimateCrisis',
        fontWeight: '400',
        textTransform: 'uppercase',
        textAlign: 'center',
        lineHeight: 48,
    },
    pageSubtitle: {
        color: 'rgba(255, 255, 255, 0.70)',
        fontSize: 18,
        fontFamily: 'Slate',
        fontWeight: '400',
        textAlign: 'center',
        lineHeight: 24,
        maxWidth: 600,
    },
    scrollContainer: {
        flex: 1,
        width: '100%',
    },
    scrollContent: {
        paddingBottom: 40,
    },
    cardContainer: {
        width: '100%',
        flexDirection: 'row',
        gap: 30,
        justifyContent: 'center',
        paddingVertical: 20,
        flexWrap: 'wrap',
    },
    card: {
        flex: 1,
        minWidth: 320,
        maxWidth: 400,
        position: 'relative',
        backgroundColor: 'rgba(0, 0, 0, 0)',
        borderRadius: 20,
        borderWidth: 1,
        borderBottomWidth: 2,
        borderTopColor: 'rgba(255,255,255,0.25)',
        borderLeftColor: 'rgba(255,255,255,0.18)',
        borderRightColor: 'rgba(255,255,255,0.12)',
        borderBottomColor: 'rgba(255,255,255,0.08)',
        marginBottom: 20,
    },
    cardBlur: {
        flex: 1,
        width: '100%',
        borderRadius: 20,
        padding: 24,
        overflow: 'hidden',
    },
    currentPlanCard: {
        borderColor: 'white',
    },
    cardContent: {
        flex: 1,
        flexDirection: 'column',
        alignItems: 'center',
        gap: 24,
        paddingVertical: 8,
    },
    headerSection: {
        alignItems: 'center',
        gap: 8,
        minHeight: 120,
        justifyContent: 'center',
    },
    planName: {
        color: 'rgba(255, 255, 255, 0.90)',
        fontSize: 32,
        fontFamily: 'ClimateCrisis',
        fontWeight: '400',
        textTransform: 'uppercase',
        textAlign: 'center',
        lineHeight: 32,
    },
    planPrice: {
        color: 'white',
        fontSize: 54,
        fontFamily: 'Slate',
        fontWeight: '700',
        marginTop: 50,
        marginBottom: 5,
        textAlign: 'center',
        lineHeight: 48,
    },
    planPeriod: {
        color: 'rgba(255, 255, 255, 0.60)',
        fontSize: 16,
        fontFamily: 'Slate',
        fontWeight: '400',
        textAlign: 'center',
        marginBottom: 40,
        lineHeight: 20,
    },
    featuresContainer: {
        width: '100%',
        flex: 1,
        gap: 12,
    },
    featureItem: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 12,
    },
    checkmark: {
        color: '#4CAF50',
        fontSize: 16,
        fontFamily: 'Slate',
        fontWeight: '700',
        marginTop: 2,
        minWidth: 16,
    },
    featureText: {
        color: 'rgba(255, 255, 255, 0.80)',
        fontSize: 14,
        fontFamily: 'Slate',
        fontWeight: '400',
        lineHeight: 20,
        flex: 1,
    },
    subscribeButton: {
        width: '100%',
        paddingVertical: 16,
        paddingHorizontal: 24,
    },
    subscribeButtonText: {
        textTransform: 'uppercase',
        letterSpacing: 1,
    },
    freeNoticeContainer: {
        width: '100%',
        maxWidth: 600,
        alignSelf: 'center',
        marginTop: 40,
        marginBottom: 20,
        padding: 24,
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        borderRadius: 16,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
    },
    freeNoticeTitle: {
        color: 'rgba(255, 255, 255, 0.90)',
        fontSize: 24,
        fontFamily: 'ClimateCrisis',
        fontWeight: '400',
        textAlign: 'center',
        marginBottom: 12,
    },
    freeNoticeText: {
        color: 'rgba(255, 255, 255, 0.70)',
        fontSize: 16,
        fontFamily: 'Slate',
        fontWeight: '400',
        textAlign: 'center',
        lineHeight: 24,
    },
});

export default PlansScreen;
